import {
  OtpLog,
  OtpLogsResponse,
  OtpAnalytics,
  OtpAnalyticsOverview,
  OtpEventType,
  OtpStatus,
  OtpChannel,
  OtpTimeSeriesData,
} from "@/pages/otp-log/types/otp-log";

// Generate mock OTP logs
function generateMockLogs(count: number = 50): OtpLog[] {
  const eventTypes = [
    OtpEventType.OTP_SENT,
    OtpEventType.OTP_DELIVERED,
    OtpEventType.OTP_VERIFIED,
    OtpEventType.OTP_FAILED,
    OtpEventType.OTP_EXPIRED,
  ];
  const statuses = [OtpStatus.SUCCESS, OtpStatus.FAILED, OtpStatus.PENDING];
  const channels = [OtpChannel.SMS, OtpChannel.EMAIL, OtpChannel.WHATSAPP];
  const providers = ["Twilio", "SendGrid", "AWS SNS", "Vonage", null];
  
  const names = [
    "John Doe",
    "Jane Smith",
    "Alice Johnson",
    "Bob Williams",
    "Charlie Brown",
    "Diana Prince",
    "Eve Adams",
    "Frank Miller",
    "Grace Lee",
    "Henry Davis",
  ];
  
  const emails = [
    "john.doe@example.com",
    "jane.smith@example.com",
    "alice.j@example.com",
    "bob.williams@example.com",
    "charlie.b@example.com",
    "diana.p@example.com",
    "eve.adams@example.com",
    "frank.m@example.com",
    "grace.lee@example.com",
    "henry.d@example.com",
  ];

  const logs: OtpLog[] = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const daysAgo = Math.floor(i / 5);
    const hoursAgo = i % 24;
    const createdAt = new Date(now);
    createdAt.setDate(createdAt.getDate() - daysAgo);
    createdAt.setHours(createdAt.getHours() - hoursAgo);
    createdAt.setMinutes(createdAt.getMinutes() - (i % 60));

    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const channel = channels[Math.floor(Math.random() * channels.length)];
    const provider = providers[Math.floor(Math.random() * providers.length)];
    const userIndex = i % names.length;
    const hasUser = Math.random() > 0.2; // 80% have user data

    const log: OtpLog = {
      id: `log-${i + 1}`,
      otpless_user_id: hasUser ? `user-${userIndex + 1}` : undefined,
      otpless_identifier: hasUser
        ? undefined
        : `+1${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      session_token: `session-${Math.random().toString(36).substring(7)}`,
      otp_code: Math.floor(100000 + Math.random() * 900000).toString(),
      event_type: eventType,
      status: status,
      channel: channel,
      provider: provider || undefined,
      delivery_time_ms:
        eventType === OtpEventType.OTP_DELIVERED ||
        eventType === OtpEventType.OTP_VERIFIED
          ? Math.floor(Math.random() * 5000) + 500
          : undefined,
      verification_time_ms:
        eventType === OtpEventType.OTP_VERIFIED
          ? Math.floor(Math.random() * 10000) + 2000
          : undefined,
      ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      user_agent:
        i % 3 === 0
          ? "Mozilla/5.0 (iPhone; CPU iPhone OS 14_0 like Mac OS X) AppleWebKit/605.1.15"
          : i % 3 === 1
          ? "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
          : "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
      error_message:
        status === OtpStatus.FAILED
          ? ["Invalid OTP", "OTP expired", "Network error", "Rate limit exceeded"][
              Math.floor(Math.random() * 4)
            ]
          : undefined,
      metadata:
        Math.random() > 0.7
          ? {
              attempt_count: Math.floor(Math.random() * 3) + 1,
              device_id: `device-${Math.random().toString(36).substring(7)}`,
            }
          : undefined,
      created_at: createdAt.toISOString(),
      updated_at: createdAt.toISOString(),
      user: hasUser
        ? {
            id: `user-${userIndex + 1}`,
            full_name: names[userIndex],
            preferred_email: emails[userIndex],
            preferred_phone_number: `+1${Math.floor(Math.random() * 9000000000) + 1000000000}`,
          }
        : undefined,
    };

    logs.push(log);
  }

  return logs.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

// Generate time series data for the last 30 days
function generateTimeSeriesData(): OtpTimeSeriesData[] {
  const data: OtpTimeSeriesData[] = [];
  const now = new Date();

  for (let i = 29; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    const baseValue = 50 + Math.floor(Math.random() * 100);
    const sent = baseValue;
    const delivered = Math.floor(sent * (0.95 + Math.random() * 0.05));
    const verified = Math.floor(delivered * (0.85 + Math.random() * 0.1));
    const failed = sent - delivered + Math.floor(Math.random() * 5);

    data.push({
      date: date.toISOString().split("T")[0],
      total: sent,
      sent: sent,
      delivered: delivered,
      verified: verified,
      failed: failed,
    });
  }

  return data;
}

// Mock data exports
export const mockOtpLogs = generateMockLogs(150);

export function getMockOtpLogsResponse(
  page: number = 0,
  pageSize: number = 20,
  filters?: {
    search?: string;
    event_type?: OtpEventType | "ALL";
    status?: OtpStatus | "ALL";
    channel?: OtpChannel | "ALL";
    startDate?: string;
    endDate?: string;
  }
): OtpLogsResponse {
  let filteredLogs = [...mockOtpLogs];

  // Apply filters
  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    filteredLogs = filteredLogs.filter(
      (log) =>
        log.user?.full_name?.toLowerCase().includes(searchLower) ||
        log.user?.preferred_email?.toLowerCase().includes(searchLower) ||
        log.otpless_identifier?.toLowerCase().includes(searchLower) ||
        log.session_token?.toLowerCase().includes(searchLower)
    );
  }

  if (filters?.event_type && filters.event_type !== "ALL") {
    filteredLogs = filteredLogs.filter((log) => log.event_type === filters.event_type);
  }

  if (filters?.status && filters.status !== "ALL") {
    filteredLogs = filteredLogs.filter((log) => log.status === filters.status);
  }

  if (filters?.channel && filters.channel !== "ALL") {
    filteredLogs = filteredLogs.filter((log) => log.channel === filters.channel);
  }

  if (filters?.startDate) {
    const startDate = new Date(filters.startDate);
    filteredLogs = filteredLogs.filter(
      (log) => new Date(log.created_at) >= startDate
    );
  }

  if (filters?.endDate) {
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);
    filteredLogs = filteredLogs.filter(
      (log) => new Date(log.created_at) <= endDate
    );
  }

  // Pagination
  const total = filteredLogs.length;
  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);
  const lastPage = Math.ceil(total / pageSize) - 1;

  return {
    logs: paginatedLogs,
    pagination: {
      currentPage: page,
      lastPage: lastPage,
      total: total,
    },
  };
}

export function getMockOtpLogById(id: string): OtpLog | null {
  return mockOtpLogs.find((log) => log.id === id) || null;
}

export function getMockOtpLogsByUserId(userId: string): OtpLog[] {
  return mockOtpLogs.filter((log) => log.otpless_user_id === userId);
}

export function getMockOtpAnalyticsOverview(): OtpAnalyticsOverview {
  const sent = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_SENT
  ).length;
  const delivered = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_DELIVERED
  ).length;
  const verified = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_VERIFIED
  ).length;
  const failed = mockOtpLogs.filter(
    (log) => log.status === OtpStatus.FAILED
  ).length;
  const total = mockOtpLogs.length;

  const deliveryTimes = mockOtpLogs
    .map((log) => log.delivery_time_ms)
    .filter((time): time is number => time !== undefined);
  const verificationTimes = mockOtpLogs
    .map((log) => log.verification_time_ms)
    .filter((time): time is number => time !== undefined);

  return {
    total: total,
    sent: sent,
    delivered: delivered,
    verified: verified,
    failed: failed,
    successRate: delivered > 0 ? (verified / delivered) * 100 : 0,
    deliveryRate: sent > 0 ? (delivered / sent) * 100 : 0,
    avgDeliveryTimeMs:
      deliveryTimes.length > 0
        ? deliveryTimes.reduce((a, b) => a + b, 0) / deliveryTimes.length
        : 0,
    avgVerificationTimeMs:
      verificationTimes.length > 0
        ? verificationTimes.reduce((a, b) => a + b, 0) / verificationTimes.length
        : 0,
  };
}

export function getMockOtpAnalytics(): OtpAnalytics {
  const overview = getMockOtpAnalyticsOverview();
  const timeSeries = generateTimeSeriesData();

  const channelBreakdown = {
    SMS: mockOtpLogs.filter((log) => log.channel === OtpChannel.SMS).length,
    EMAIL: mockOtpLogs.filter((log) => log.channel === OtpChannel.EMAIL).length,
    WHATSAPP: mockOtpLogs.filter((log) => log.channel === OtpChannel.WHATSAPP)
      .length,
  };

  return {
    overview,
    timeSeries,
    channelBreakdown,
  };
}

export function getMockOtpDeliveryRate(): {
  rate: number;
  delivered: number;
  sent: number;
} {
  const sent = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_SENT
  ).length;
  const delivered = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_DELIVERED
  ).length;

  return {
    sent,
    delivered,
    rate: sent > 0 ? (delivered / sent) * 100 : 0,
  };
}

export function getMockOtpSuccessRate(): {
  rate: number;
  verified: number;
  delivered: number;
} {
  const delivered = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_DELIVERED
  ).length;
  const verified = mockOtpLogs.filter(
    (log) => log.event_type === OtpEventType.OTP_VERIFIED
  ).length;

  return {
    delivered,
    verified,
    rate: delivered > 0 ? (verified / delivered) * 100 : 0,
  };
}

export function getMockOtpChannelBreakdown(): {
  SMS: number;
  EMAIL: number;
  WHATSAPP: number;
} {
  return {
    SMS: mockOtpLogs.filter((log) => log.channel === OtpChannel.SMS).length,
    EMAIL: mockOtpLogs.filter((log) => log.channel === OtpChannel.EMAIL).length,
    WHATSAPP: mockOtpLogs.filter((log) => log.channel === OtpChannel.WHATSAPP)
      .length,
  };
}

export function getMockOtpStats(): OtpAnalyticsOverview {
  return getMockOtpAnalyticsOverview();
}

